import type { Referral } from '../services/apiService';

/**
 * AXISREF437: ICPMSK referrals routed to the Miscellaneous queue bypass AI Triage
 * by design, so a complete ICP miscellaneous referral may be completed directly
 * from that queue without first passing through `ready_for_indici`. Completion in
 * this path never sends the referral to Indici — the client must show the
 * no-Indici confirmation before sending the explicit backend flag.
 */
export function canCompleteMiscIcpWithoutIndici(
  referral: Pick<
    Referral,
    'status' | 'is_complete' | 'is_miscellaneous' | 'miscellaneous_reason'
  >,
): boolean {
  if (referral.is_complete !== true) {
    return false;
  }
  const status = String(referral.status || '');
  if (status === 'completed') {
    return false;
  }
  // Referrals already in the standard completion flow keep the standard gate
  // and the standard confirmation copy (AXISREF437 targets ICP referrals that
  // bypass AI Triage and therefore never reached ready_for_indici).
  if (status === 'ready_for_indici' || status === 'awaiting_reply') {
    return false;
  }
  if (referral.is_miscellaneous !== true) {
    return false;
  }
  return String(referral.miscellaneous_reason || '')
    .trim()
    .toLowerCase() === 'icp';
}

export const SR_COMPLETION_OUTBOUND_WARNING =
  'Referral completed in Axis, but the SR response was not accepted. The referral is in Completed. Use Message GP or contact support if the response must be delivered.';

export interface MarkCompleteConfirmCopy {
  isMiscIcp: boolean;
  message: string;
  confirmLabel: string;
}

/**
 * Returns the confirmation dialog copy for marking a referral complete.
 * The Miscellaneous/ICP path uses the approved no-Indici wording; Cancel is the
 * safe default and only the explicit confirm action mutates the referral.
 */
export function getMarkCompleteConfirmCopy(isMiscIcp: boolean): MarkCompleteConfirmCopy {
  if (isMiscIcp) {
    return {
      isMiscIcp: true,
      message:
        'Complete this referral? This will mark it as completed in Axis and remove it from the Miscellaneous Queue. It will not be sent to Indici.',
      confirmLabel: 'Complete without sending to Indici',
    };
  }
  return {
    isMiscIcp: false,
    message:
      'Are you sure you want to mark this referral as completed? This indicates it has been entered into Indici.',
    confirmLabel: 'Mark Complete',
  };
}
